// React
import { useEffect, useState } from "react";

// Third-party libraries
import clsx from "clsx";
import moment, { Moment } from "moment";

// MUI
import { useGridApiRef, GridColDef, GridValidRowModel, GridGroupingColDefOverride, GridRenderCellParams } from "@mui/x-data-grid-premium";
import { Box, Tooltip } from "@mui/material";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import { TypographyCell } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";

// Icons
import { ReactComponent as AttentionIcon } from "@/assets/icons/dataPages/inventory/information.svg";

// Types
import { IDepartmentPositionRecord, IPtoTimeToShow, IPtoDetails } from "@/DataPages/types/timeAndPayRates/models";

// Constants
import { COLUMN_RESIZE_FACTOR, HEADER_ALIGN_RIGHT, HEADER_CLASS_NAME, TAILWIND_FOR_DATA_SMART_TABLE } from "@/Constants/tables";
import { prepareGroupColDef } from "@/Layouts/components/TreeGrid/prepareGroupColDef";
import { tableKeys } from "@/Constants/tableKeys";
import { DATE_FORMAT } from "@/Constants/date";

const ONE_HUNDRED_FIFTEEN = 115;

const getToolTipTitle = (params: GridRenderCellParams) => {
	const matchingDetail = params.row.details.find((detail: IPtoDetails) => detail.type === params.row.fullName);
	if (matchingDetail?.approvedDates?.length) {
		const formattedDates = matchingDetail.approvedDates.map((date: Moment) => moment.utc(date).format(DATE_FORMAT.usDateSlash));
		return formattedDates.join("\n");
	}
	return "";
};

const columns: GridColDef[] = [
	{
		field: "_id",
		headerName: "ID",
		filterable: false,
	},
	{
		field: "searchName",
		filterable: false,
	},
	{
		field: "fullName",
		headerName: "Associate Name",
		minWidth: ONE_HUNDRED_FIFTEEN,
		width: ONE_HUNDRED_FIFTEEN,
		maxWidth: ONE_HUNDRED_FIFTEEN * COLUMN_RESIZE_FACTOR,
		sortable: false,
		headerClassName: HEADER_CLASS_NAME,
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => {
			const containsWord = params.id.toString().includes("request_") || params.id.toString().includes("last_row_details_");
			const isChildRow = params.id.toString().includes("child_");
			const approvedDates = getToolTipTitle(params);
			if (containsWord) {
				const textInTwoLines = params.value.split(" ");
				return (
					<TypographyHeader className="!pt-2">
						{textInTwoLines[0]}
						<br />
						{textInTwoLines[1]}
					</TypographyHeader>
				);
			}

			return (
				<div className="flex items-center">
					{isChildRow && approvedDates && (
						<Tooltip title={<div style={{ whiteSpace: "pre-line" }}>{approvedDates}</div>} placement="bottom" arrow>
							<Box component="div" className="absolute left-[10px] mt-1">
								<AttentionIcon className={"fill-gray-text"} />
							</Box>
						</Tooltip>
					)}
					<CustomCell value={params.value} hasTooltip maxWith={params.colDef.width ?? ONE_HUNDRED_FIFTEEN} fontSize="12px" />
				</div>
			);
		},
	},
	{
		field: "balance",
		headerName: "Available Balance",
		minWidth: 80,
		width: 80,
		align: "right",
		type: "number",
		headerClassName: HEADER_CLASS_NAME + HEADER_ALIGN_RIGHT,
		sortable: false,
		renderHeader: (params) => <TypographyHeader params={params} />,
		renderCell: (params) => {
			const containsWord = params.id.toString().includes("request_") || params.id.toString().includes("last_row_details_");
			if (containsWord) {
				return "";
			}
			if (!params.value) {
				const value = "0.00";
				return (
					<TypographyCell
						render="account"
						params={params}
						issues={[]}
						fontSize={"12px"}
						formatOptions={{ decimals: 2 }}
						hints={{ isFunded: false }}
					>
						{value}
					</TypographyCell>
				);
			}
			return (
				<TypographyCell
					params={params}
					render="account"
					hints={{ isFunded: false }}
					fontSize={"12px"}
					formatOptions={{ decimals: 2 }}
					issues={[]}
				/>
			);
		},
	},
	{
		field: "requested",
		headerName: "Approved Request",
		minWidth: 90,
		width: 90,
		renderHeader: (params) => <TypographyHeader params={params} />,
		sortable: false,
		headerClassName: HEADER_CLASS_NAME + HEADER_ALIGN_RIGHT,
		align: "right",
		type: "number",
		renderCell: (params) => {
			const containsWord = params.id.toString().includes("request_") || params.id.toString().includes("last_row_details_");
			if (containsWord) {
				return "";
			}
			if (!params.value) {
				const value = "0.00";
				return (
					<TypographyCell
						formatOptions={{ decimals: 2 }}
						hints={{ isFunded: false }}
						params={params}
						issues={[]}
						render="account"
						fontSize={"12px"}
					>
						{value}
					</TypographyCell>
				);
			}
			return (
				<TypographyCell
					params={params}
					formatOptions={{ decimals: 2 }}
					issues={[]}
					hints={{ isFunded: false }}
					fontSize={"12px"}
					render="account"
				/>
			);
		},
	},
];

export const isNavigationKey = (key: string) => {
	const navigationKeys = ["Home", "End", " "];
	return navigationKeys.includes(key) || key.startsWith("Arrow") || key.startsWith("Page");
};

const getTreeDataPath = (row: GridValidRowModel) => row.hierarchy;

interface IPtoTableProps {
	data: IDepartmentPositionRecord[];
	filterKeySuffix?: string;
}

export default function PtoTable(props: Readonly<IPtoTableProps>) {
	const { data } = props;

	const apiRef = useGridApiRef();
	const [rows, setRows] = useState<IPtoTimeToShow[]>([]);

	const groupingColDef: GridGroupingColDefOverride = {
		...prepareGroupColDef({ expandedAll: false }),
		cellClassName: "!border-none",
	};

	useEffect(() => {
		const dataToShow: IPtoTimeToShow[] = [];
		if (!data?.length) {
			setRows([]);
			return;
		}
		data.forEach((item) => {
			dataToShow.push({
				...item,
				hierarchy: [item._id],
				searchName: item.fullName,
			});

			if (!item.details?.length) {
				return;
			}

			dataToShow.push({
				_id: `request_${item._id}`,
				hierarchy: [item._id, `request_${item._id}`],
				fullName: "Request Type",
				searchName: item.fullName,
				balance: "",
				requested: "",
				details: [],
			});
			item.details.forEach((detail) => {
				dataToShow.push({
					...detail,
					_id: `child_${item._id}_${detail.typeId}`,
					hierarchy: [item._id, `child_${item._id}_${detail.typeId}`],
					fullName: detail.type,
					searchName: item.fullName,
					details: item.details,
				});
			});
			dataToShow.push({
				_id: `last_row_details_${item._id}`,
				hierarchy: [item._id, `last_row_details_${item._id}`],
				fullName: "",
				searchName: item.fullName,
				balance: "",
				requested: "",
				details: [],
				lastRow: true,
			});
		});
		setRows(dataToShow);
	}, [data]);

	return (
		<DataSmartTable
			filterStoreConfig={{
				key: `${tableKeys.DATA_PAGES.TIME_PAY_RATES_PTO}/${props.filterKeySuffix}`,
			}}
			apiRef={apiRef}
			treeData
			rows={rows}
			columns={columns}
			maxRows={18}
			hideFooter
			enableCustomScrollbars
			enableRowsFiller={true}
			disableColumnMenu
			disableRowSelectionOnClick
			disableMultipleRowSelection
			checkboxSelection={false}
			getTreeDataPath={getTreeDataPath}
			groupingColDef={groupingColDef}
			getRowClassName={(params) => {
				const isRequestRow = typeof params.id === "string" && params.id.includes("request_");
				return clsx({
					"[&_.MuiDataGrid-cell]:!bg-white [&_.MuiDataGrid-scrollbarFiller]:!bg-white": params.row?.lastRow || isRequestRow,
					"[&_.MuiDataGrid-cell]:!shadow-[inset_0_-1px_0_0_#CCC] [&_.MuiDataGrid-scrollbarFiller]:!shadow-[inset_0_-1px_0_0_#CCC]":
						isRequestRow,
				});
			}}
			getRowHeight={(params) => {
				if (typeof params.id === "string" && params.id.includes("child_")) {
					return 25;
				}
				if (typeof params.id === "string" && params.id.includes("request_")) {
					return 50;
				}
				if (typeof params.id === "string" && params.id.includes("last_row_details_")) {
					return 25;
				}
				return 25;
			}}
			columnVisibilityModel={{
				_id: false,
				searchName: false,
			}}
			slots={{
				toolbar: CommonToolbar,
				noRowsOverlay: NoRowsOverlay,
			}}
			slotProps={{
				toolbar: {
					titleExpandible: {
						className: "[&_.toolbar-actions]:!right-0 [&_.toolbar-actions]:!bottom-0",
						hide: true,
					},
					filter: {
						hide: false,
					},
					search: {
						useQuickFilter: true,
					},
				},
				noRowsOverlay: {
					title: "No PTO Data",
				},
			}}
			customProps={{
				tableContainerStyle: { height: "100%", width: "100%" },
				tableClassName: {
					...TAILWIND_FOR_DATA_SMART_TABLE,
					"[&_.toolbar-actions]:!top-[0px]": true,
					"[&_.MuiDataGrid-main]:mt-[16px]": true,
				},
				hide: false,
				expandableRows: true,
			}}
		/>
	);
}
